import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Language, AIFollowUpQuestion } from '@nordcrest/shared';
import { getQuestionsBySection, sections } from '@nordcrest/shared';

export type SurveyPhase = 'intro' | 'questions' | 'ai-loading' | 'ai-questions' | 'contact' | 'complete';

interface AnswerValue {
  value: string | string[] | number | boolean | null;
  answeredAt: string;
}

interface SurveyState {
  // Survey identification
  surveyId: string | null;

  // Current state
  phase: SurveyPhase;
  language: Language;
  currentSection: number;
  currentQuestionIndex: number;

  // Answers
  answers: Record<string, AnswerValue>;
  aiFollowUpQuestions: AIFollowUpQuestion[];
  aiFollowUpAnswers: Record<string, AnswerValue>;

  // Loading states
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  // Completion state
  isComplete: boolean;
  wantsCallback: boolean;

  // Actions
  setSurveyId: (id: string) => void;
  setPhase: (phase: SurveyPhase) => void;
  setLanguage: (language: Language) => void;
  setCurrentSection: (section: number) => void;
  setCurrentQuestionIndex: (index: number) => void;
  setAnswer: (questionId: string, value: string | string[] | number | boolean | null) => void;
  setAIFollowUpQuestions: (questions: AIFollowUpQuestion[]) => void;
  setAIFollowUpAnswer: (questionId: string, value: string | string[] | number | boolean | null) => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setError: (error: string | null) => void;
  setComplete: (wantsCallback: boolean) => void;
  reset: () => void;

  // Computed helpers
  getCurrentSectionQuestions: () => ReturnType<typeof getQuestionsBySection>;
  getCurrentQuestion: () => ReturnType<typeof getQuestionsBySection>[number] | null;
  getProgress: () => { current: number; total: number; percentage: number };
  canGoNext: () => boolean;
  canGoPrev: () => boolean;
}

const initialState = {
  surveyId: null,
  phase: 'intro' as SurveyPhase,
  language: 'sv' as Language,
  currentSection: 1,
  currentQuestionIndex: 0,
  answers: {},
  aiFollowUpQuestions: [],
  aiFollowUpAnswers: {},
  isLoading: false,
  isSaving: false,
  error: null,
  isComplete: false,
  wantsCallback: false,
};

export const useSurveyStore = create<SurveyState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSurveyId: (id) => set({ surveyId: id }),

      setPhase: (phase) => set({ phase }),

      setLanguage: (language) => set({ language }),

      setCurrentSection: (section) => set({ currentSection: section, currentQuestionIndex: 0 }),

      setCurrentQuestionIndex: (index) => set({ currentQuestionIndex: index }),

      setAnswer: (questionId, value) =>
        set((state) => ({
          answers: {
            ...state.answers,
            [questionId]: {
              value,
              answeredAt: new Date().toISOString(),
            },
          },
        })),

      setAIFollowUpQuestions: (questions) => set({ aiFollowUpQuestions: questions }),

      setAIFollowUpAnswer: (questionId, value) =>
        set((state) => ({
          aiFollowUpAnswers: {
            ...state.aiFollowUpAnswers,
            [questionId]: {
              value,
              answeredAt: new Date().toISOString(),
            },
          },
        })),

      setLoading: (loading) => set({ isLoading: loading }),

      setSaving: (saving) => set({ isSaving: saving }),

      setError: (error) => set({ error }),

      setComplete: (wantsCallback) =>
        set({ isComplete: true, wantsCallback, phase: 'complete' }),

      reset: () => set(initialState),

      getCurrentSectionQuestions: () => {
        const { currentSection } = get();
        return getQuestionsBySection(currentSection);
      },

      getCurrentQuestion: () => {
        const { currentSection, currentQuestionIndex } = get();
        const questions = getQuestionsBySection(currentSection);
        return questions[currentQuestionIndex] || null;
      },

      getProgress: () => {
        const { currentSection, currentQuestionIndex, phase, aiFollowUpQuestions } = get();

        // Calculate total questions across sections 1, 2, and 4
        const section1Questions = getQuestionsBySection(1).length;
        const section2Questions = getQuestionsBySection(2).length;
        const section4Questions = getQuestionsBySection(4).length;
        const aiQuestionCount = aiFollowUpQuestions.length;

        const totalQuestions =
          section1Questions + section2Questions + aiQuestionCount + section4Questions;

        let currentQuestion = 0;

        if (phase === 'intro') {
          currentQuestion = 0;
        } else if (currentSection === 1) {
          currentQuestion = currentQuestionIndex + 1;
        } else if (currentSection === 2) {
          currentQuestion = section1Questions + currentQuestionIndex + 1;
        } else if (phase === 'ai-questions') {
          currentQuestion = section1Questions + section2Questions + currentQuestionIndex + 1;
        } else if (currentSection === 4 || phase === 'contact') {
          currentQuestion =
            section1Questions + section2Questions + aiQuestionCount + currentQuestionIndex + 1;
        }

        const percentage = totalQuestions > 0 ? Math.round((currentQuestion / totalQuestions) * 100) : 0;

        return { current: currentQuestion, total: totalQuestions, percentage };
      },

      canGoNext: () => {
        const state = get();
        const question = state.getCurrentQuestion();

        if (!question) return false;

        const answer = state.answers[question.id];

        // If required, check if answered
        if (question.required) {
          if (!answer || answer.value === null || answer.value === undefined) {
            return false;
          }
          if (typeof answer.value === 'string' && answer.value.trim() === '') {
            return false;
          }
          if (Array.isArray(answer.value) && answer.value.length === 0) {
            return false;
          }
        }

        return true;
      },

      canGoPrev: () => {
        const { currentSection, currentQuestionIndex, phase } = get();

        if (phase === 'intro') return false;
        if (currentSection === 1 && currentQuestionIndex === 0) return false;

        return true;
      },
    }),
    {
      name: 'nordcrest-survey',
      partialize: (state) => ({
        surveyId: state.surveyId,
        language: state.language,
        currentSection: state.currentSection,
        currentQuestionIndex: state.currentQuestionIndex,
        answers: state.answers,
        aiFollowUpQuestions: state.aiFollowUpQuestions,
        aiFollowUpAnswers: state.aiFollowUpAnswers,
        phase: state.phase,
        isComplete: state.isComplete,
      }),
    }
  )
);
